import { useState } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { Head1 } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { toast } from 'react-toastify'
import {
  foodExceptionsMock,
  foodInitialConfirmations,
  foodKitchenOrderedMock,
  foodKitchensMock,
  foodMealRequestsMock,
  foodMealWindowsMock,
} from './foodData'
import { IFoodKitchen, IKitchenConfirmation } from 'types/foodLogistics/foodKitchen.interface'
import { IFoodException } from 'types/foodLogistics/foodException.interface'
import { IFoodMealRequest } from 'types/foodLogistics/foodMealRequest.interface'
import { DashboardTab } from './tabs/DashboardTab'
import { ExceptionsTab } from './tabs/ExceptionsTab'
import { MealRequestsTab } from './tabs/MealRequestsTab'
import { ProfilesTab } from './tabs/ProfilesTab'
import { ConfigurationTab } from './tabs/ConfigurationTab'
import { HistoryTab } from './tabs/HistoryTab'

const FOOD_TABS = ['Dashboard', 'Exceptions', 'Meal Requests', 'Profiles', 'Configuration', 'History']

export const Food = () => {
  const [activeTab, setActiveTab] = useState('Dashboard')
  const [windows] = useState(foodMealWindowsMock)
  const [kitchens, setKitchens] = useState<IFoodKitchen[]>(foodKitchensMock)
  const [exceptions, setExceptions] = useState<IFoodException[]>(foodExceptionsMock)
  const [requests, setRequests] = useState<IFoodMealRequest[]>(foodMealRequestsMock)
  const [confirmations] = useState<{ [key: string]: IKitchenConfirmation }>(foodInitialConfirmations)
  const [createMealRequestOpen, setCreateMealRequestOpen] = useState(false)
  const [configurationToolbar, setConfigurationToolbar] = useState<React.ReactNode>(null)

  const resolveException = (exc: IFoodException, actionKey: string, comment: string) => {
    setExceptions((es) => es.map((e) => (e.id === exc.id ? { ...e, status: 'Resolved', res: actionKey } : e)))
    toast.success(`Exception #${exc.id} resolved via ${actionKey}${comment ? ' — ' + comment : ''}`)
  }

  const decideRequest = (id: string, approve: boolean) => {
    setRequests((rs) =>
      rs.map((r) =>
        r.id === id ? { ...r, status: approve ? 'Approved' : 'Rejected', routing: approve ? 'Standby' : '—' } : r,
      ),
    )
    toast.success(approve ? `${id} approved · routing: STANDBY` : `${id} rejected`)
  }

  const goCreateRequest = () => {
    setActiveTab('Meal Requests')
    setCreateMealRequestOpen(true)
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    if (tab !== 'Configuration') setConfigurationToolbar(null)
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16'>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Food</Head1>
        </Flex>
        <Flex between alignEnd mb='16' width='100%' wrap gap='1rem'>
          <TabsContainer>
            {FOOD_TABS.map((tab) => (
              <Tabs key={tab} active={activeTab === tab} onClick={() => handleTabChange(tab)}>
                {tab}
              </Tabs>
            ))}
          </TabsContainer>
          {activeTab === 'Dashboard' && (
            <Button variant='primary' style={{ width: '220px' }} onClick={goCreateRequest}>
              Create Meal Request
            </Button>
          )}
          {activeTab === 'Meal Requests' && (
            <Button variant='primary' style={{ width: '220px' }} onClick={() => setCreateMealRequestOpen(true)}>
              Create Meal Request
            </Button>
          )}
          {activeTab === 'Configuration' && configurationToolbar}
          {activeTab === 'History' && <Button variant='primary'>Export</Button>}
        </Flex>
        {activeTab === 'Dashboard' && (
          <DashboardTab
            windows={windows}
            kitchens={kitchens}
            kitchenOrdered={foodKitchenOrderedMock}
            confirmations={confirmations}
            exceptions={exceptions}
            openExceptions={() => setActiveTab('Exceptions')}
          />
        )}
        {activeTab === 'Exceptions' && (
          <ExceptionsTab exceptions={exceptions} resolveException={resolveException} />
        )}
        {activeTab === 'Meal Requests' && (
          <MealRequestsTab
            requests={requests}
            setRequests={setRequests}
            decideRequest={decideRequest}
            kitchens={kitchens}
            windows={windows}
            createOpen={createMealRequestOpen}
            setCreateOpen={setCreateMealRequestOpen}
          />
        )}
        {activeTab === 'Profiles' && <ProfilesTab />}
        {activeTab === 'Configuration' && (
          <ConfigurationTab
            kitchens={kitchens}
            setKitchens={setKitchens}
            windows={windows}
            onToolbarChange={setConfigurationToolbar}
          />
        )}
        {activeTab === 'History' && <HistoryTab />}
      </PageWrapper>
    </>
  )
}
